/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { baseApiService, type QueryParams } from './base'

export interface GroupFilter extends QueryParams {
  id?: number[]
  type?: string[]
  [key: string]: string | number | boolean | string[] | number[] | null | undefined
}

export interface Group {
  id: number
  name: string
  type?: string
  description?: string
  createdAt?: number
  createdBy?: { id: number; name: string }
  updatedAt?: number
  updatedBy?: { id: number; name: string }
  createdById?: number
  updatedById?: number
  userIds?: number[]
  systemGroup?: boolean
  deleted?: boolean
}

export interface GroupsResponse {
  totalElements?: number
  elements: Group[]
}

export interface PickerFilter extends QueryParams {
  name?: string
  [key: string]: string | number | boolean | string[] | number[] | null | undefined
}

export interface PickerElement {
  id: number
  name: string
}

export interface PickerResponse {
  totalElements?: number
  elements: PickerElement[]
}

export interface PickerOption {
  value: number
  label: string
}

export interface PickerOptionsResponse {
  options: PickerOption[]
  hasMore: boolean
}

export interface ReportFilter {
  userId?: number
  groupId?: number
  filterId?: number
  [key: string]: string | number | boolean | string[] | number[] | null | undefined
}

export interface ReportMeta {
  pageSize?: number
  pageStart?: number
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | string[] | number[] | null | undefined
}

export interface GroupUsage {
  id?: number
  name?: string
}

export interface GroupUsageResponse {
  fsisWithGroupAcl?: GroupUsage[]
  fsisWithGroupApprover?: GroupUsage[]
  fsisWithGroupTrainee?: GroupUsage[]
}

export interface CreateUpdateGroup {
  name?: string
  description?: string
  userIds?: number[]
  createRATasksNow?: boolean
  type?: string
  permissions?: string[]
  eosFolderId?: number
  [key: string]: unknown
}

export interface UpdateGroup {
  name?: string
  description?: string
  userIds?: number[]
  createRATasksNow?: boolean
  type?: string
  permissions?: string[]
  eosFolderId?: number
}

class GroupsApiService {
  async getOptionsForAsyncPicker(
    searchTerm?: string,
    pageStart = 0,
    additionalParams: Partial<PickerFilter> = {}
  ): Promise<PickerOptionsResponse> {
    const params: Partial<PickerFilter> = { pageStart, pageSize: 25 }

    if (searchTerm) {
      params.name = searchTerm
    }

    const response = await baseApiService.fetch<PickerResponse>('/api/v2/groups/picker', {
      method: 'GET',
      params: { ...params, ...additionalParams }
    })

    const hasMore = Boolean(response.elements.length && (pageStart + 1) * 25 < (response.totalElements || 0))

    const options = response.elements
      .map((i) => ({ value: i.id, label: i.name }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))

    return {
      options,
      hasMore
    }
  }

  async getList(filter?: GroupFilter): Promise<GroupsResponse> {
    return baseApiService.fetch('/api/v2/groups', {
      method: 'GET',
      params: filter
    })
  }

  async getForPicker(filter?: PickerFilter): Promise<PickerResponse> {
    return baseApiService.fetch('/api/v2/groups/picker', {
      method: 'GET',
      params: filter
    })
  }

  async exportGroupMembershipReports(filter: ReportFilter, meta: ReportMeta): Promise<unknown> {
    return baseApiService.fetch('/api/reports/groupexport', {
      method: 'POST',
      body: { meta, filter }
    })
  }

  async getGroupMembershipReports(filter: ReportFilter, meta: ReportMeta): Promise<unknown> {
    return baseApiService.fetch('/api/reports/groupreport', {
      method: 'POST',
      body: { meta, filter }
    })
  }

  // This api is not used anywhere in the app
  async getUsersInGroup(groupId: number): Promise<unknown> {
    return baseApiService.fetch(`/api/groups/${groupId}/users`, {
      method: 'GET'
    })
  }

  // This api is not used anywhere in the app
  async addUserToGroup(groupId: number, userId: number): Promise<unknown> {
    return baseApiService.fetch(`/api/groups/${groupId}/users`, {
      method: 'POST',
      body: { userId }
    })
  }

  // This api is not used anywhere in the app
  async addMultipleUsersToGroup(groupId: number, usersIds: number[]): Promise<unknown> {
    return baseApiService.fetch(`/api/groups/${groupId}/users`, {
      method: 'POST',
      body: { users: usersIds }
    })
  }

  // This api is not used anywhere in the app
  async removeUserFromGroup(groupId: number, userId: number): Promise<void> {
    return baseApiService.fetch(`/api/groups/${groupId}/users/${userId}`, {
      method: 'DELETE'
    })
  }

  // This api is not used anywhere in the app
  async getAll(filter?: GroupFilter): Promise<GroupsResponse> {
    const defaultFilter = { sortBy: 'name', sortDesc: false }
    return baseApiService.fetch('/api/groups', {
      method: 'GET',
      params: { ...defaultFilter, ...filter }
    })
  }

  async getAllForDropdowns(filter?: GroupFilter, withType = false): Promise<GroupsResponse> {
    const defaultFilter = { sortBy: 'name', sortDesc: false }
    const endpoint = withType ? '/api/groups/allwithtype' : '/api/groups/all'
    return baseApiService.fetch(endpoint, {
      method: 'GET',
      params: { ...defaultFilter, ...filter }
    })
  }

  async getAllForDropdownsWithType(filter?: GroupFilter): Promise<GroupsResponse> {
    return this.getAllForDropdowns(filter, true)
  }

  async getById(groupId: number): Promise<Group> {
    return baseApiService.fetch(`/api/groups/${groupId}`, {
      method: 'GET'
    })
  }

  async getGroupUsage(groupId: number): Promise<GroupUsageResponse> {
    return baseApiService.fetch(`/api/groups/${groupId}/usage`, {
      method: 'GET'
    })
  }

  async create(data: CreateUpdateGroup): Promise<Group> {
    return baseApiService.fetch('/api/groups', {
      method: 'POST',
      body: data
    })
  }

  async update(groupId: number, data: CreateUpdateGroup): Promise<Group> {
    return baseApiService.fetch(`/api/groups/${groupId}`, {
      method: 'PUT',
      body: data
    })
  }

  async remove(groupId: number): Promise<void> {
    return baseApiService.fetch(`/api/groups/${groupId}`, {
      method: 'DELETE'
    })
  }
}

export const groupsApiService = new GroupsApiService()
